import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { formatDay, formatDayRange, localDay } from '../utils/insightsDates';
import { useInsightsAccess } from './useInsightsAccess';
import { isAllowedGapsLens } from './useLens';

// Shared time-period state for the Insights dashboards. Persists in the URL
// (?from=YYYY-MM-DD&to=YYYY-MM-DD&period=3m) so the browser back/forward
// buttons restore the user's range as part of the same history step that
// changed it.

export type PresetId =
  | '1w'
  | '1m'
  | '3m'
  | '6m'
  | 'term'
  | 'ytd'
  | '1y'
  | 'all'
  | 'custom';

export type TimePeriod = {
  from: string | null;
  to: string | null;
  preset: PresetId;
};

export const DEFAULT_PRESET: PresetId = 'term';

export const PRESET_IDS: PresetId[] = [
  '1w',
  '1m',
  '3m',
  '6m',
  'term',
  'ytd',
  '1y',
  'all',
  'custom',
];

// Labels for the fixed-window presets. `term` is deliberately absent: it is
// named after the half of the school year it covers ("Fall 2026"), so its
// label is generated in `presetLabel` from today's date instead.
const FIXED_PRESET_LABELS: Record<Exclude<PresetId, 'term'>, string> = {
  '1w': 'Last week',
  '1m': 'Last month',
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  ytd: 'Since Jan 1',
  '1y': 'Last year',
  all: 'All time',
  custom: 'Custom range',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Format a Date as YYYY-MM-DD in the user's local timezone. Using
// `toISOString()` would emit UTC, which silently shifts the from/to window
// by a day for US admins on the wrong side of midnight UTC.
function isoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// The school year splits in two: the fall half opens Aug 1, the spring half
// Jan 1. The dashboard default starts at whichever of those the viewer is
// currently in, so an admin never opens on a window that mixes in the
// previous half's roster. Built as a literal string rather than through a
// Date so it can't drift a day in either direction the way `toISOString()`
// does.
const FALL_TERM_START_MONTH = 7; // August, 0-indexed

export function termStartIso(today: Date): string {
  const year = today.getFullYear();
  return today.getMonth() >= FALL_TERM_START_MONTH ? `${year}-08-01` : `${year}-01-01`;
}

// 'term' is named the way US schools name the half it covers: "Fall 2026"
// from Aug 1, "Spring 2027" from Jan 1 — derived from the same start date as
// its window, so the label can't disagree with it. Every UI call site goes
// through here.
export function presetLabel(preset: PresetId, today = new Date()): string {
  if (preset !== 'term') return FIXED_PRESET_LABELS[preset];
  const start = termStartIso(today);
  const season = start.endsWith('-08-01') ? 'Fall' : 'Spring';
  return `${season} ${start.slice(0, 4)}`;
}

export function presetRange(
  preset: PresetId,
): { from: string | null; to: string | null } {
  const today = new Date();
  const to = isoDate(today);
  if (preset === 'all') return { from: null, to: null };
  if (preset === 'custom') return { from: null, to: null };
  if (preset === 'term') {
    return { from: termStartIso(today), to };
  }
  if (preset === 'ytd') {
    return { from: `${today.getFullYear()}-01-01`, to };
  }
  const days =
    preset === '1w'
      ? 7
      : preset === '1m'
      ? 30
      : preset === '3m'
      ? 90
      : preset === '6m'
      ? 180
      : 365;
  // Both ends are inclusive and today is one of the days, so "Last week" on
  // Oct 15 starts Oct 9, not Oct 8.
  const from = isoDate(new Date(today.getTime() - (days - 1) * MS_PER_DAY));
  return { from, to };
}

// Build a `?key=value&…` suffix from the current period state so internal
// links can carry the user's range across navigation. Returns `""` when on
// the default preset so default URLs stay clean.
export function periodQuerySuffix(
  period: TimePeriod,
  extra: Record<string, string> = {},
): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(extra)) {
    if (v) params.set(k, v);
  }
  if (period.preset !== DEFAULT_PRESET) params.set('period', period.preset);
  if (period.preset === 'custom') {
    if (period.from) params.set('from', period.from);
    if (period.to) params.set('to', period.to);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useTimePeriod(): {
  period: TimePeriod;
  setPreset: (preset: PresetId) => void;
  setCustomRange: (from: string, to: string) => void;
  /** Query suffix to forward the active period through internal links. */
  querySuffix: (extra?: Record<string, string>) => string;
} {
  const router = useRouter();

  const period: TimePeriod = useMemo(() => {
    const presetParam = (router.query.period as string | undefined) ?? DEFAULT_PRESET;
    const isValidPreset = PRESET_IDS.includes(presetParam as PresetId);
    const preset = (isValidPreset ? presetParam : DEFAULT_PRESET) as PresetId;
    if (preset === 'custom') {
      return {
        from: (router.query.from as string | undefined) ?? null,
        to: (router.query.to as string | undefined) ?? null,
        preset,
      };
    }
    const range = presetRange(preset);
    return { from: range.from, to: range.to, preset };
  }, [router.query.period, router.query.from, router.query.to]);

  const writeQuery = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      // Merge with current query and replace so we stay in the same history slot
      // when the user changes the range. We use `replace` so a single back-press
      // returns to whatever page they were on, not to an earlier filter.
      const next = { ...router.query, ...patch } as Record<string, string | undefined>;
      // Strip nulls/empty so the URL stays clean for the default state.
      for (const k of Object.keys(next)) {
        const v = next[k];
        if (v == null || v === '') delete next[k];
      }
      router.replace({ pathname: router.pathname, query: next }, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router],
  );

  const setPreset = useCallback(
    (preset: PresetId) => {
      if (preset === DEFAULT_PRESET) {
        // Clean URL for the default — no ?period=3m noise.
        writeQuery({ period: null, from: null, to: null });
      } else if (preset === 'custom') {
        // No range yet — caller must follow up with setCustomRange.
        writeQuery({ period: 'custom' });
      } else {
        writeQuery({ period: preset, from: null, to: null });
      }
    },
    [writeQuery],
  );

  const setCustomRange = useCallback(
    (from: string, to: string) => {
      writeQuery({ period: 'custom', from, to });
    },
    [writeQuery],
  );

  // Pull rubric off the URL so drilldown links carry it automatically. We
  // read it here (rather than via useRubric) to keep this hook the single
  // source of truth for the link suffix — callers don't need to remember to
  // pass the active rubric explicitly.
  // Any non-default family travels: a Georgia page writes `?rubric=GMAS`, and a
  // link that dropped it would name a different URL than the page it opens
  // (Back compares them) and refetch SCR before reconciling back to GMAS.
  const rubricParam = router.query.rubric;
  const rubricExtra: Record<string, string> =
    rubricParam === 'ECR' || rubricParam === 'GMAS' ? { rubric: rubricParam } : {};
  // Carry the active lens across drilldown only when the user is authorized for it.
  // Without the flag check, a stale ?lens=gaps URL would silently propagate to users
  // who can't see the gaps lens.
  const access = useInsightsAccess();
  const lensParam = router.query.lens;
  const lensExtra: Record<string, string> = isAllowedGapsLens(lensParam, access)
    ? { lens: lensParam }
    : {};
  const querySuffix = useCallback(
    (extra: Record<string, string> = {}) =>
      periodQuerySuffix(period, { ...rubricExtra, ...lensExtra, ...extra }),
    [period, rubricExtra.rubric, lensExtra.lens],
  );

  return { period, setPreset, setCustomRange, querySuffix };
}

// Pretty-print a period for the dashboard headline. `Last 3 months` for
// presets, or "Feb 18 – May 18, 2026" for custom ranges.
export function formatPeriod(period: TimePeriod): string {
  if (period.preset !== 'custom') return presetLabel(period.preset);
  if (!period.from || !period.to) return 'Custom range';
  return formatDayRange(period.from, period.to);
}

// Format for a sentence — fits the slot "taught no students {here}". "in the
// last 3 months", "in Fall 2026", "since Jan 1, 2026", "between Feb 18 and May
// 18, 2026", or "across all Curipod lessons". Dates read as everywhere else
// (`utils/insightsDates`).
export function formatPeriodInSentence(period: TimePeriod): string {
  if (period.preset === 'all') return 'across all Curipod lessons';
  if (period.preset === 'custom') {
    if (!period.from || !period.to) return 'in the selected period';
    if (period.from === period.to) return `on ${formatDay(period.from)}`;
    return `between ${formatDayRange(period.from, period.to, { joiner: ' and ' })}`;
  }
  if (period.preset === 'term') {
    // Named as on the pill. Read off the resolved window rather than today's
    // date so a shared "?period=term" link reads the same as the dashboard it
    // came from.
    return `in ${presetLabel('term', localDay(period.from ?? termStartIso(new Date())))}`;
  }
  if (period.preset === 'ytd') {
    const year = (period.from ?? termStartIso(new Date())).slice(0, 4);
    return `since ${formatDay(`${year}-01-01`)}`;
  }
  const dur =
    period.preset === '1w'
      ? 'week'
      : period.preset === '1m'
      ? 'month'
      : period.preset === '3m'
      ? '3 months'
      : period.preset === '6m'
      ? '6 months'
      : 'year';
  return `in the last ${dur}`;
}
